import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, ArrowLeft, Loader2, Bot } from "lucide-react";
import { services } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function newSessionId() {
  try {
    const existing = window.localStorage.getItem("gabicom_chat_session");
    if (existing) return existing;
  } catch (e) {
    /* ignore */
  }
  const id = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : "sess-" + Math.random().toString(36).slice(2) + Date.now();
  try {
    window.localStorage.setItem("gabicom_chat_session", id);
  } catch (e) {
    /* ignore */
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [service, setService] = useState(null);
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const startChat = (svc) => {
    setService(svc);
    setMessages([
      {
        role: "assistant",
        content: `Hi! I'm Gabi, your Gabicom assistant. I'd be glad to help with ${svc}. What can I do for you?`,
      },
    ]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/chat`,
        { session_id: sessionId, message: text, service },
        { timeout: 45000 }
      );
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      console.error("Chat error:", e);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I'm having trouble right now. Please email sales@gabicomitsolutions.com or call 1.202.309.2266 and our team will help you.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-launcher"
        aria-label="Open support chat"
        className="fixed bottom-6 right-6 z-[60] flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#00f0ff] to-[#b026ff] text-[#05050a] shadow-2xl shadow-[#b026ff]/40"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-7 w-7" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle className="h-7 w-7" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            data-testid="chat-panel"
            className="glass fixed bottom-24 right-6 z-[60] flex h-[520px] w-[90vw] max-w-[380px] flex-col overflow-hidden rounded-3xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-white/10 bg-[#0d0e15]/80 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#00f0ff] to-[#b026ff] text-[#05050a]">
                <Bot className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-display text-base leading-none text-white">Gabicom Support</p>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-[#a0aec0]">
                  <span className="h-2 w-2 rounded-full bg-[#00f0ff]" /> Online now
                </p>
              </div>
              {service && (
                <button
                  onClick={() => {
                    setService(null);
                    setMessages([]);
                  }}
                  data-testid="chat-change-service"
                  className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs text-[#a0aec0] transition-colors hover:text-white"
                >
                  <ArrowLeft className="h-3 w-3" /> Service
                </button>
              )}
            </div>

            {/* Body */}
            {!service ? (
              <div className="flex-1 overflow-y-auto p-5" data-testid="chat-service-select">
                <p className="text-sm text-[#a0aec0]">
                  Welcome to Gabicom! Which service can we help you with today?
                </p>
                <div className="mt-4 grid gap-2.5">
                  {services.map((s) => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.slug}
                        onClick={() => startChat(s.title)}
                        data-testid={`chat-service-${s.slug}`}
                        className="group flex items-center gap-3 rounded-xl border border-white/10 bg-[#0d0e15] p-3 text-left transition-colors hover:border-[#00f0ff]/50"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-[#00f0ff] transition-colors group-hover:text-[#b026ff]">
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="text-sm text-white">{s.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          m.role === "user"
                            ? "bg-gradient-to-br from-[#00f0ff] to-[#b026ff] text-[#05050a]"
                            : "border border-white/10 bg-[#0d0e15] text-[#e2e8f0]"
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-[#0d0e15] px-4 py-3 text-[#a0aec0]">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-xs">Gabi is typing…</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-white/10 p-3">
                  <div className="flex items-end gap-2">
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKey}
                      rows={1}
                      data-testid="chat-input"
                      placeholder="Type your message…"
                      className="max-h-24 flex-1 resize-none rounded-xl border border-white/10 bg-[#0d0e15] px-3 py-2.5 text-sm text-white placeholder:text-[#5a6478] outline-none focus:ring-2 focus:ring-[#b026ff]"
                    />
                    <button
                      onClick={send}
                      disabled={loading || !input.trim()}
                      data-testid="chat-send"
                      className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#00f0ff] to-[#b026ff] text-[#05050a] transition-transform hover:scale-105 disabled:opacity-50"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
